import React from 'react';
import { Helmet } from 'react-helmet';

const About = () => {
  return (
    <>
      <Helmet>
        <title>About | Psychological Manipulation</title>

        <meta
          name="description"
          content="Learn about Psychological Manipulation, an evidence-informed educational resource examining coercion, manipulation, power, autonomy, and interpersonal behavior through psychological and sociological perspectives."
        />
      </Helmet>

      <main className="topic-page">
        <header>
          <p className="articleEyebrow">
            Sociology · Psychology · Human Behavior
          </p>

          <h1>About This Site</h1>

          <p className="articleLead">
            Psychological Manipulation is an independent educational resource
            examining how influence can become coercion, how power operates
            within relationships, and how sustained manipulation can affect
            judgment, autonomy, identity, and social functioning.
          </p>
        </header>

        <section>
          <h2>Why This Site Exists</h2>

          <p>
            Manipulation is often discussed in simplified terms, yet the
            behaviors involved can be psychologically and socially complex.
            Gaslighting, coercive control, emotional pressure, triangulation,
            isolation, intimidation, and other patterns may develop gradually
            and can be difficult to recognize when viewed as isolated events.
          </p>

          <p>
            This site was created to examine those behaviors carefully:
            defining concepts, distinguishing established clinical terminology
            from popular language, identifying what research supports, and
            exploring the effects that manipulative and coercive dynamics can
            have on individuals and relationships.
          </p>
        </section>

        <section>
          <h2>A Sociological Lens</h2>

          <p>
            I am Pamela Terrell, and I hold a B.A. in Sociology. That background
            shapes the way I approach this subject.
          </p>

          <p>
            Manipulation does not occur only inside individual personalities.
            It can also be shaped by power differences, social expectations,
            family systems, dependency, workplace hierarchy, gender roles,
            economic conditions, institutional authority, and access to social
            support.
          </p>

          <p>
            For that reason, this site looks at both interpersonal behavior and
            the broader environments in which that behavior occurs.
          </p>
        </section>

        <section>
          <h2>Evidence Before Labels</h2>

          <p>
            One of the central principles of this site is that observable
            behavior should be distinguished from diagnosis.
          </p>

          <p>
            A person can engage in gaslighting, intimidation, exploitation,
            coercion, or repeated boundary violations without anyone outside a
            clinical setting being able to determine whether that person has a
            personality disorder.
          </p>

          <p>
            Likewise, terms that are common in online relationship discussions
            are not always established clinical categories. Where appropriate,
            articles on this site identify those distinctions explicitly.
          </p>

          <div className="researchNote">
            <strong>Editorial principle:</strong> When research supports a
            claim, the goal is to say so clearly. When a concept is debated,
            emerging, informal, or insufficiently studied, that uncertainty
            should also be made clear.
          </div>
        </section>

        <section>
          <h2>Why the Effects Matter</h2>

          <p>
            The significance of psychological manipulation lies not only in
            what a person does, but in what repeated exposure can do to another
            person&apos;s ability to think, decide, trust, connect, and act
            independently.
          </p>

          <p>
            Sustained coercion or psychological abuse may contribute to
            confusion, chronic fear, self-doubt, social isolation, reduced
            confidence in decision-making, and other forms of psychological
            distress.
          </p>

          <p>
            Understanding those effects can be more useful than relying on
            dramatic personality labels or assuming that every harmful behavior
            has the same motive.
          </p>
        </section>

        <section>
          <h2>Who This Site Is For</h2>

          <p>
            This resource is intended for anyone seeking to understand
            psychological manipulation and interpersonal control more clearly.
          </p>

          <p>
            That may include someone examining a current or past relationship,
            a student researching human behavior, a family member trying to
            understand a difficult dynamic, or simply a reader interested in
            how power and influence operate between people.
          </p>

          <p>
            The aim is not to tell readers what conclusion to reach about a
            particular person. It is to provide concepts, distinctions, and
            research that can support more informed thinking.
          </p>
        </section>

        <section className="educationalDisclaimer">
          <strong>Educational scope:</strong> Psychological Manipulation is an
          educational resource and does not provide mental health diagnosis,
          treatment, legal advice, or individualized safety assessment. Anyone
          experiencing threats, stalking, violence, or other immediate safety
          concerns should seek appropriate professional or emergency
          assistance.
        </section>

        <section>
          <h2>About the Project</h2>

          <p>
            Psychological Manipulation is an independent educational project
            from StabileUSA.com.
          </p>

          <p>
            The broader aim is to make complex human behavior easier to examine
            without reducing people or relationships to simplistic labels.
          </p>
        </section>

        <section className="researchNote">
          <strong>Clear terminology matters. Context matters. Evidence matters.</strong>
          <br />
          Understanding how power, influence, and manipulation operate can make
          confusing behavior easier to evaluate.
        </section>
      </main>
    </>
  );
};

export default About;